import { downloadCsv } from './csv';
import {
    CoaImportRow, OpeningDocumentRow, OpeningTrialBalanceRow,
} from '../interfaces/openingBalance.interface';

/** RFC-4180 CSV parser (quoted fields, escaped quotes, CRLF/LF, BOM). Tab-separated text (pasted from a sheet) works too. */
export const parseCsv = (text: string): string[][] => {
    const src = text.replace(/^﻿/, '');
    const firstLine = src.split(/\r?\n/, 1)[0] ?? '';
    const delimiter = firstLine.includes('\t') && !firstLine.includes(',') ? '\t' : ',';
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < src.length; i++) {
        const c = src[i];
        if (inQuotes) {
            if (c === '"') {
                if (src[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
            } else {
                field += c;
            }
        } else if (c === '"') {
            inQuotes = true;
        } else if (c === delimiter) {
            row.push(field); field = '';
        } else if (c === '\n' || c === '\r') {
            if (c === '\r' && src[i + 1] === '\n') i++;
            row.push(field); field = '';
            rows.push(row); row = [];
        } else {
            field += c;
        }
    }
    if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
    return rows.filter(r => r.some(cell => cell.trim() !== ''));
};

const norm = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, '');

interface Column<K extends string> { key: K; aliases: string[] }

/**
 * Maps CSV rows to objects by header name (aliases, case and punctuation insensitive). Returns the
 * rows plus the headers that were required but missing, so the UI can say exactly what is wrong.
 */
const mapRows = <K extends string>(text: string, columns: Column<K>[], required: K[]):
    { rows: Record<K, string>[]; missing: string[] } => {
    const table = parseCsv(text);
    if (table.length === 0) return { rows: [], missing: [] };
    const header = table[0].map(norm);
    const index = {} as Record<K, number>;
    columns.forEach(col => {
        index[col.key] = header.findIndex(h => col.aliases.map(norm).includes(h));
    });
    const missing = required.filter(k => index[k] < 0).map(k => columns.find(c => c.key === k)!.aliases[0]);
    if (missing.length > 0) return { rows: [], missing };
    const rows = table.slice(1).map(cells => {
        const out = {} as Record<K, string>;
        columns.forEach(col => { out[col.key] = index[col.key] >= 0 ? (cells[index[col.key]] ?? '').trim() : ''; });
        return out;
    });
    return { rows, missing: [] };
};

export const COA_COLUMNS: Column<keyof CoaImportRow>[] = [
    { key: 'code', aliases: ['code', 'account code'] },
    { key: 'name', aliases: ['name', 'account name'] },
    { key: 'type', aliases: ['type', 'account type'] },
    { key: 'parentCode', aliases: ['parent code', 'parent'] },
    { key: 'kind', aliases: ['posting/group', 'kind', 'posting group'] },
];
export const parseCoaCsv = (text: string) => mapRows(text, COA_COLUMNS, ['code', 'name', 'type']);

export const TB_COLUMNS: Column<keyof OpeningTrialBalanceRow>[] = [
    { key: 'accountCode', aliases: ['account code', 'code'] },
    { key: 'debit', aliases: ['debit', 'dr'] },
    { key: 'credit', aliases: ['credit', 'cr'] },
];
export const parseTrialBalanceCsv = (text: string) => mapRows(text, TB_COLUMNS, ['accountCode']);

export const documentColumns = (party: 'customer' | 'supplier'): Column<keyof OpeningDocumentRow>[] => [
    { key: 'partyName', aliases: [party, `${party} name`, 'name'] },
    { key: 'documentNumber', aliases: ['document no', 'document number', 'invoice no', 'bill no', 'doc no'] },
    { key: 'documentDate', aliases: ['date', 'document date', 'invoice date', 'bill date'] },
    { key: 'dueDate', aliases: ['due date', 'due'] },
    { key: 'amount', aliases: ['amount', 'outstanding', 'balance'] },
];
export const parseDocumentsCsv = (text: string, party: 'customer' | 'supplier') =>
    mapRows(text, documentColumns(party), ['partyName', 'documentNumber', 'documentDate', 'amount']);

export const TEMPLATES = {
    coa: {
        file: 'chart-of-accounts-template.csv',
        headers: ['Code', 'Name', 'Type', 'Parent Code', 'Posting/Group'],
        rows: [
            ['1001000000', 'Current Assets', 'ASSET', '', 'GROUP'],
            ['1001010100', 'Cash in Hand', 'ASSET', '1001000000', 'POSTING'],
        ],
    },
    trialBalance: {
        file: 'opening-trial-balance-template.csv',
        headers: ['Account Code', 'Debit', 'Credit'],
        rows: [['1001010100', '50000', ''], ['3001010100', '', '50000']],
    },
    customers: {
        file: 'opening-customer-invoices-template.csv',
        headers: ['Customer', 'Document No', 'Date', 'Due Date', 'Amount'],
        rows: [['Acme Ltd', 'INV-OLD-101', '2026-05-10', '2026-06-09', '25000']],
    },
    suppliers: {
        file: 'opening-supplier-bills-template.csv',
        headers: ['Supplier', 'Document No', 'Date', 'Due Date', 'Amount'],
        rows: [['Fabric Co', 'SUP-77', '2026-05-20', '2026-06-19', '40000']],
    },
};

export const downloadTemplate = (key: keyof typeof TEMPLATES) => {
    const t = TEMPLATES[key];
    downloadCsv(t.file, t.headers, t.rows);
};
